import { useId, useLayoutEffect, useState, type ReactElement, type RefObject } from "react";

/** Text of the card shown under the hovered month box. */
export type LinkTooltip = { title: string; lines: string[] };

type Geometry = {
  column: { x: number; y: number; width: number; height: number } | null;
  card: { left: number; top: number } | null;
  links: Array<{ key: string; band: string | null; line: string; from: [number, number]; to: [number, number] }>;
};

const EMPTY: Geometry = { column: null, card: null, links: [] };

/**
 * Links a hovered month box to the payments that fill it, drawn over the opened family: a light column over the
 * month, a soft band with a dashed line to each payment, and a card with the month's details. Nothing moves; it
 * only fades in.
 */
export default function PaymentLinks({ containerRef, sourceSelector, receiptIds, tooltip }: {
  containerRef: RefObject<HTMLElement>;
  /** Selector of the hovered month box inside the container; null when nothing is hovered. */
  sourceSelector: string | null;
  receiptIds: string[];
  tooltip?: LinkTooltip | null;
}): ReactElement | null {
  const [geometry, setGeometry] = useState<Geometry>(EMPTY);
  const arrowId = `finance-link-arrow-${useId().replace(/:/g, "")}`;
  const targetsKey = receiptIds.join(",");

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const source = sourceSelector ? container?.querySelector<HTMLElement>(sourceSelector) : null;
      if (!container || !source) {
        setGeometry(EMPTY);
        return;
      }
      const box = container.getBoundingClientRect();
      const from = source.getBoundingClientRect();
      const month = (source.closest("[data-cell-month]") as HTMLElement | null)?.getBoundingClientRect() ?? from;
      const months = (container.querySelector("[data-months-column]") as HTMLElement | null)?.getBoundingClientRect();

      const links: Geometry["links"] = [];
      (targetsKey ? targetsKey.split(",") : []).forEach((id) => {
        const target = container.querySelector<HTMLElement>(`[data-receipt="${id}"]`);
        if (!target) return;
        const to = target.getBoundingClientRect();
        if (to.left >= from.right) {
          // Payments next to the months: a band that widens from the box to the whole payment row.
          const x1 = from.right - box.left;
          const x2 = to.left - box.left;
          const yFrom = from.top + from.height / 2 - box.top;
          const yTo = to.top + to.height / 2 - box.top;
          const bend = Math.max(48, (x2 - x1) / 2);
          const top1 = from.top - box.top;
          const bottom1 = from.bottom - box.top;
          const top2 = to.top - box.top;
          const bottom2 = to.bottom - box.top;
          links.push({
            key: id,
            band: `M ${x1} ${top1} C ${x1 + bend} ${top1}, ${x2 - bend} ${top2}, ${x2} ${top2} `
              + `L ${x2} ${bottom2} C ${x2 - bend} ${bottom2}, ${x1 + bend} ${bottom1}, ${x1} ${bottom1} Z`,
            line: `M ${x1} ${yFrom} C ${x1 + bend} ${yFrom}, ${x2 - bend} ${yTo}, ${x2} ${yTo}`,
            from: [x1, yFrom],
            to: [x2, yTo],
          });
        } else {
          // Payments below the months (narrow screens): only a line downwards.
          const x1 = from.left + from.width / 2 - box.left;
          const y1 = from.bottom - box.top;
          const x2 = to.left + 16 - box.left;
          const y2 = to.top - box.top;
          const bend = Math.max(30, (y2 - y1) / 2);
          links.push({ key: id, band: null, line: `M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2}`, from: [x1, y1], to: [x2, y2] });
        }
      });

      setGeometry({
        column: months
          ? { x: month.left - box.left - 3, y: months.top - box.top, width: month.width + 6, height: months.height }
          : null,
        card: { left: Math.max(0, month.left - box.left - 8), top: from.bottom - box.top + 10 },
        links,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [containerRef, sourceSelector, targetsKey]);

  if (!sourceSelector || (!geometry.column && geometry.links.length === 0)) return null;
  return (
    <>
      <style>{`
        @keyframes lumenFinanceLinkFade { from { opacity: 0; } to { opacity: 1; } }
        .lumen-finance-link { animation: lumenFinanceLinkFade 140ms ease-out both; }
        @media (prefers-reduced-motion: reduce) { .lumen-finance-link { animation: none; } }
      `}</style>
      <svg key={sourceSelector} aria-hidden="true" className="lumen-finance-link pointer-events-none absolute inset-0 z-10 h-full w-full overflow-visible">
        <defs>
          <marker id={arrowId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 8 4 L 0 8 z" className="fill-amber-500 dark:fill-amber-300" />
          </marker>
        </defs>
        {geometry.column && (
          <rect x={geometry.column.x} y={geometry.column.y} width={geometry.column.width} height={geometry.column.height}
            rx={8} className="fill-amber-200/25 dark:fill-amber-300/10" />
        )}
        {geometry.links.map((link) => (
          <g key={link.key}>
            {link.band && <path d={link.band} className="fill-amber-200/40 dark:fill-amber-300/10" />}
            <path d={link.line} fill="none" strokeWidth={1.5} strokeDasharray="5 5" strokeLinecap="round"
              markerEnd={`url(#${arrowId})`} className="stroke-amber-500 dark:stroke-amber-300" />
            <circle cx={link.from[0]} cy={link.from[1]} r={3.5} className="fill-amber-500 dark:fill-amber-300" />
            <circle cx={link.to[0]} cy={link.to[1]} r={5} strokeWidth={2.5}
              className="fill-white stroke-amber-500 dark:fill-slate-900 dark:stroke-amber-300" />
          </g>
        ))}
      </svg>
      {tooltip && geometry.card && (
        <div role="tooltip" key={`card-${sourceSelector}`}
          style={{ left: geometry.card.left, top: geometry.card.top }}
          className="lumen-finance-link pointer-events-none absolute z-20 w-72 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-700 shadow-lg dark:border-white/10 dark:bg-slate-900 dark:text-slate-200">
          <p className="text-sm font-bold text-slate-900 dark:text-white">{tooltip.title}</p>
          {tooltip.lines.map((line) => <p key={line} className="mt-0.5">{line}</p>)}
        </div>
      )}
    </>
  );
}
